
import React from "react";
import { Target } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { athleteTierSchema } from "@/contexts/program-creation/types";

interface PerformanceGoalsSectionProps {
  form: UseFormReturn<z.infer<typeof athleteTierSchema>>;
}

const performanceAreas = [
  { id: "strength", label: "Strength" },
  { id: "power", label: "Power" },
  { id: "speed_agility", label: "Speed/Agility" },
  { id: "endurance", label: "Endurance" },
  { id: "flexibility", label: "Flexibility" }
];

export default function PerformanceGoalsSection({ form }: PerformanceGoalsSectionProps) {
  return (
    <div className="space-y-5 mb-8">
      <div className="flex items-center gap-2 text-white/90 border-b border-white/20 pb-2">
        <Target size={18} />
        <h4 className="font-medium">Performance Goals</h4>
      </div>
      
      <FormField
        control={form.control}
        name="sportSpecificGoals"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white/90 text-sm">
              What performance goals are you focusing on for their sport?
            </FormLabel>
            <FormControl>
              <Textarea 
                placeholder="e.g., Improve their maximum vertical jump height for high jump or Increase aerobic capacity for rugby" 
                className="bg-white/20 border-white/30 text-white min-h-[120px]"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <FormField
        control={form.control}
        name="keyPerformanceAreas"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white/90 text-sm">
              Which areas do you want to improve? (Select all that apply)
            </FormLabel>
            <div className="mt-2 space-y-3">
              {performanceAreas.map((area) => (
                <div key={area.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`performance-${area.id}`}
                    checked={field.value?.includes(area.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        field.onChange([...field.value, area.id]);
                      } else {
                        field.onChange(
                          field.value?.filter((value) => value !== area.id)
                        );
                      }
                    }}
                    className="border-white/50 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                  />
                  <label
                    htmlFor={`performance-${area.id}`}
                    className="text-sm text-white font-medium cursor-pointer"
                  >
                    {area.label}
                  </label>
                </div>
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
